import { memo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { NoPostsFound } from "@/react/components/HomePageV2/NoPostsFound";
import { withErrorBoundary } from "@/react/components/shared/ErrorPages/WithErrorBoundary";
import type { PostSort } from "@/react/types/CurrentCommunity";
import { usePostList } from "@circle-react/components/HomePageV2/usePostList";
import { CurrentSpaceContextProvider } from "@circle-react/contexts";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { PostNavigationProvider } from "../PostsV3/PostViewList/PostNavigationContext";
import { LoaderWrapper } from "./LoaderWrapper";
import { ViewItem } from "./ViewItem";

export interface PostListProps {
  defaultSort: PostSort;
}

const PostListComponent = memo(({ defaultSort }: PostListProps) => {
  const {
    fetchNextPage,
    hasNextPage,
    isLoading,
    isLoadingAppData,
    onPostChange,
    onPostDestroy,
    perPagePostIds,
    posts,
    refetchPosts,
    shouldShowLoader,
  } = usePostList(defaultSort);

  if (shouldShowLoader || isLoading || isLoadingAppData) {
    return <LoaderWrapper />;
  }

  if (!posts.length) {
    return <NoPostsFound />;
  }

  return (
    <PostsDetailsContextProvider perPagePostIds={perPagePostIds}>
      <PostNavigationProvider params={defaultSort} hasNextPage={hasNextPage}>
        <div
          className="space__posts space__posts--posts !pb-16 lg:!pb-6"
          data-testid="post_section"
        >
          <InfiniteScroll
            style={{ overflow: "initial" }}
            scrollThreshold={0.5}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<LoaderRow />}
            dataLength={posts.length}
            scrollableTarget="panel"
          >
            {posts.map(post => (
              <CurrentSpaceContextProvider
                key={post.id}
                spaceId={post.space_id}
              >
                <ViewItem
                  isRenderedOutsideTheSpace
                  key={post.id}
                  onPostChange={onPostChange}
                  onPostDestroy={onPostDestroy}
                  refetchPosts={refetchPosts}
                  post={post}
                />
              </CurrentSpaceContextProvider>
            ))}
          </InfiniteScroll>
        </div>
      </PostNavigationProvider>
    </PostsDetailsContextProvider>
  );
});
PostListComponent.displayName = "PostList";

export const PostList = withErrorBoundary(PostListComponent, LoaderWrapper);
